import CaretLoader from '@root/core/src/components/caret-loader';
import DontSeeYourJobSection from '@root/joinroot.com/src/components/careers/dont-see-your-job-section';
import JobDescription from '@root/joinroot.com/src/components/careers/job-description';
import JobListingHeader from '@root/joinroot.com/src/components/careers/job-listing-header';
import LinkButtonModel from '@root/gatsby-contentful-core/src/models/link-button';
import OpenPositionsSection from '@root/careers/src/components/icons/open-positions/open-positions-section';
import PageTemplate from '@root/joinroot.com/src/components/pages/page-template';
import PropTypes from '@root/vendor/prop-types';
import React, { useContext } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import SharedJobListingInfo from '@root/joinroot.com/src/components/careers/shared-job-listing-info';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { JobPostingContext, getJobPostingProvider } from '@root/careers/src/contexts/job-postings-context';
import { graphql } from 'gatsby';

export default function JobListingPage({
  data,
  location,
  pageContext: {
    jobId,
    jobTitle,
  },
}) {
  const JobProvider = getJobPostingProvider();
  const jobListingContentfulData = data.contentfulJobListingPage;

  const pageTitle = jobTitle ?
    `${jobTitle} Job Opening | Careers at Root Insurance Company` :
    jobListingContentfulData.publishingMetadata.pageTitle;

  return (
    <PageTemplate
      analyticsContext={'JOB_LISTING_PAGE'}
      location={location}
      mainStyles={mainStyles}
      pageCanonical={`https://www.joinroot.com/${location.pathname}}`}
      pageDescription={jobListingContentfulData.publishingMetadata.pageDescription}
      pageThumbnail={jobListingContentfulData.publishingMetadata.pageThumbnail?.file?.url}
      pageTitle={pageTitle}
    >
      <JobProvider
        data={data}
      >
        <JobListingPageContent
          jobId={jobId}
          jobListingContentfulData={jobListingContentfulData}
        />
      </JobProvider>
    </PageTemplate>
  );
}

JobListingPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    jobId: PropTypes.string.isRequired,
    jobTitle: PropTypes.string,
  }).isRequired,
};

const JobListingPageContent = ({
  jobListingContentfulData, jobId,
}) => {
  const { trackClick } = useAnalytics('JOB_LISTING_PAGE', false);
  const jobPostings = useContext(JobPostingContext);
  if (!jobPostings) {
    return (
      <div css={styles.loaderStyles}>
        <CaretLoader />
      </div>
    );
  }
  const jobData = jobPostings.find((job) => job.id === jobId);
  const contentfulTeamData = jobListingContentfulData.teams.find((team) => team.team === jobData.slug) || jobListingContentfulData.teams[0];

  return (
    <>
      <JobListingHeader
        headerPhoto={contentfulTeamData.headerPhoto}
        jobTitle={jobData.jobTitle}
        location={jobData.location}
        mobileHeaderPhoto={contentfulTeamData.mobileHeaderPhoto}
      />
      <JobDescription description={jobData.description} />
      <SharedJobListingInfo
        applyButtonCopy={jobListingContentfulData.applyButtonCopy}
        applyButtonDescription={jobListingContentfulData.applyButtonDescription}
        jobId={jobData.id}
        mobileSharedJobListingPhoto={jobListingContentfulData.mobileSharedJobListingPhoto}
        sharedJobListingInfoPhoto={jobListingContentfulData.sharedJobListingInfoPhoto}
        sharedJobListingText={jobListingContentfulData.sharedJobListingText}
      />
      <OpenPositionsSection trackClick={trackClick} />
      <DontSeeYourJobSection
        body={'Send your resume and what you’d love to do to jobs@inc.joinroot.com.'}
        button={{
          text: 'Email us',
          to: 'mailto:jobs@inc.joinroot.com',
          type: LinkButtonModel.types.PRIMARY,
        }}
        headline={'Don’t see your job?'}
      />
    </>
  );
};

JobListingPageContent.propTypes = {
  jobId: PropTypes.string.isRequired,
  jobListingContentfulData: PropTypes.object.isRequired,
};

const styles = {
  loaderStyles: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const mainStyles = {
  paddingTop: 50,
  width: '100vw',
  ...Responsive.md({
    paddingTop: 104,
  }),
  ...Responsive.lg({
    paddingTop: 59,
  }),
};

export const query = graphql`
query myQueryAndMyQuery {
  contentfulJobListingPage {
    applyButtonCopy
    contentful_id
    id
    applyButtonDescription {
      json
    }
    publishingMetadata {
      slug
      pageTitle
      pageDescription
      pageThumbnail {
        file {
          url
        }
      }
    }
    sharedJobListingInfoPhoto {
      fluid(maxWidth: 960, quality: 100) {
        ...GatsbyContentfulFluid_withWebp
      }
    }
    sharedJobListingText {
      json
    }
    teams {
      team
      headerPhoto {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      mobileHeaderPhoto {
        fluid(quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
    }
  }
  allLever {
    edges {
      node {
        text
        lever_id
        description
        lists {
          text
          content
        }
        categories {
          location
          team
        }
        applyUrl
      }
    }
  }
}
`;
